import { useGetGraphqlDataQuery, type UseQueryResult } from '@repo/services';
import { FILTERABLE_ENTITY_TYPES, NON_FILTERABLE_ACTIVITY_KEYS } from 'advanced-filters/data';
import { TASK_TYPE_OPTIONS } from 'advanced-filters/data/taskOptions';
import {
  getLeadActivityAttributes,
  type LeadActivityAttribute,
  type LeadActivityAttributesResponse,
} from 'advanced-filters/queries/leadActivityAttributes';
import { pendingFilterSelector } from 'advanced-filters/selectors/filter';
import { modifyPendingFilter, setFetchError } from 'advanced-filters/store/filter';
import { Select } from 'antd';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

function ActivitySubConditionKeySelect() {
  const dispatch = useDispatch();

  const {
    conditionKey,
    conditionValue,
    subConditionKey,
  } = useSelector(pendingFilterSelector);

  const {
    data: {
      activityAttribute: data = [],
    } = {},
    isLoading,
    error,
  } = useGetGraphqlDataQuery<UseQueryResult<LeadActivityAttributesResponse>>({
    document: getLeadActivityAttributes,
    variables: { activityId: parseInt(conditionValue?.value?.[0]?.value || '', 10) },
  }, {
    skip: !conditionValue || !conditionValue?.value?.[0]?.value || conditionValue?.value?.[0]?.value === 'any',
  });

  useEffect(() => {
    if (error) {
      dispatch(setFetchError('Failed To Fetch Data'));
    }
  }, [dispatch, error]);

  const handleChange = useCallback((value: string) => {
    const option = data.find((o:LeadActivityAttribute) => o.value === value);
    dispatch(modifyPendingFilter(
      { subConditionKey: option, subConditionComparator: undefined, subConditionValue: undefined },
    ));
  }, [data, dispatch]);

  const options = useMemo(() => {
    if (conditionKey?.entityType === FILTERABLE_ENTITY_TYPES.leadActivity) {
      return data.filter(
        (option: LeadActivityAttribute) => !NON_FILTERABLE_ACTIVITY_KEYS.includes(option.key),
      );
    }
    return data;
  }, [data, conditionKey]);

  return (
    <Select
      showSearch
      placeholder="Select a Condition"
      loading={isLoading}
      disabled={!data?.length}
      options={options}
      optionFilterProp="value"
      value={subConditionKey?.label || subConditionKey?.value}
      onChange={handleChange}
    />
  );
}

function TaskSubConditionKeySelect() {
  const dispatch = useDispatch();

  const {
    subConditionKey,
  } = useSelector(pendingFilterSelector);
  const handleChange = useCallback((value: string) => {
    const option = TASK_TYPE_OPTIONS.find((o) => o.value === value);
    dispatch(modifyPendingFilter(
      { subConditionKey: option, subConditionComparator: undefined, subConditionValue: undefined },
    ));
  }, [dispatch]);

  return (
    <Select
      showSearch
      placeholder="Select a Condition"
      options={TASK_TYPE_OPTIONS}
      optionFilterProp="key"
      value={subConditionKey?.label || subConditionKey?.value}
      onChange={handleChange}
    />
  );
}

export default function SubConditionKeySelect() {
  const {
    conditionKey,
  } = useSelector(pendingFilterSelector);

  if (conditionKey?.entityType === FILTERABLE_ENTITY_TYPES.taskType) {
    return <TaskSubConditionKeySelect />;
  }

  return <ActivitySubConditionKeySelect />;
}
